import { IConfiguration } from '@skoda/k2tools-app-services/lib';
import { buildSearchParamString, SearchParamsBase } from '../utils/url';

export class ApiServiceError extends Error {
	private readonly _status: number;
	private readonly _data: unknown;

	constructor(message: string, status: number, data: unknown) {
		super(message);
		this._status = status;
		this._data = data;
	}

	public responseDataAs = <T>() => {
		return this._data as T | undefined;
	};

	get responseData() {
		return this._data;
	}

	get status() {
		return this._status;
	}
}

export type ApiServiceRequest = {
	baseUrl?: string;
	params?: SearchParamsBase;
	headers?: HeadersInit;
	body?: unknown;
	noContentError?: boolean;
	signal?: AbortSignal | null;
};

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export class BaseApiService {
	protected _config: IConfiguration;

	constructor(config: IConfiguration) {
		this._config = config;
	}

	public get = <TResult>(endpoint: string, config?: ApiServiceRequest) => {
		return this.request<TResult>('GET', endpoint, config);
	};

	public post = <TResult>(endpoint: string, config?: ApiServiceRequest) => {
		return this.request<TResult>('POST', endpoint, config);
	};

	public put = <TResult = void>(endpoint: string, config?: ApiServiceRequest) => {
		return this.request<TResult>('PUT', endpoint, config);
	};

	public delete = <TResult = void>(endpoint: string, config?: ApiServiceRequest) => {
		return this.request<TResult>('DELETE', endpoint, config);
	};

	public request = async <TResult>(method: RequestMethod, endpoint: string, config?: ApiServiceRequest) => {
		const requestData = this.createRequestInit(method, config);
		const fetchUrl = this.createUrl(endpoint, config);
		const response = await fetch(fetchUrl, requestData);
		return this.processResponse<TResult>(response, config);
	};

	private processResponse = async <TResult>(response: Response, config: ApiServiceRequest | undefined) => {
		let readResult: { data: TResult; hasError: false } | { data: string; hasError: true };
		const responseText = await response.text();
		try {
			const d = JSON.parse(responseText);
			readResult = { hasError: false, data: d };
		} catch (ex) {
			readResult = { hasError: true, data: responseText };
		}

		if (!response.ok) {
			throw new ApiServiceError(response.statusText, response.status, readResult.data);
		}
		if (config?.noContentError && response.status === 204) {
			throw new ApiServiceError(response.statusText, response.status, readResult.data);
		}
		if (readResult.hasError === true) {
			return undefined as TResult;
		}

		return readResult.data;
	};

	private createRequestInit = (method: RequestMethod, config: ApiServiceRequest | undefined) => {
		const headers = new Headers(config?.headers);

		const configBody = config?.body;
		let body: RequestInit['body'];
		if (!headers.get('Content-Type') && configBody && !(configBody instanceof FormData)) {
			headers.set('Content-Type', 'application/json');
			body = JSON.stringify(configBody);
		} else {
			body = configBody as RequestInit['body'];
		}

		const reqInit: RequestInit = { method, headers, body, signal: config?.signal };
		return reqInit;
	};

	private createUrl = (endpoint: string, config: ApiServiceRequest | undefined) => {
		const baseUrl = config?.baseUrl ?? this._config.apiUrl;
		const query = buildSearchParamString(config?.params, { prependQuestionMark: true });
		const sep = baseUrl.endsWith('/') || endpoint.startsWith('/') ? '' : '/';
		return `${baseUrl}${sep}${endpoint}${query ?? ''}`;
	};
}
